/*
 * <vesper-video> — custom player for the shared video block (snippets/block-video.liquid)
 * and video sections.
 *
 * Attributes:
 *   data-autoplay="true"  muted autoplay while in view (skipped for prefers-reduced-motion)
 *   data-preview="true"   autoplay is a silent preview: the play button restarts with sound
 *   data-embed="url"      external (YouTube/Vimeo) mode: the play button swaps the poster for an iframe
 * Children (all optional): video.vvideo__media, [data-play], [data-sound], [data-timeline] > [data-progress]
 * State is exposed with [data-state="playing|paused"] and [data-muted] for styling.
 */
(() => {
  if (customElements.get('vesper-video')) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VesperVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.embed) {
        this.querySelector('[data-play]')?.addEventListener('click', () => this.loadEmbed());
        return;
      }
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playButton = this.querySelector('[data-play]');
      this.soundButton = this.querySelector('[data-sound]');
      this.timeline = this.querySelector('[data-timeline]');
      this.progress = this.querySelector('[data-progress]');
      this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
      this.preview = this.dataset.preview === 'true';
      this.previewing = this.autoplay && this.preview;
      this.userPaused = false;

      this.dataset.state = 'paused';
      this.syncMuted();

      this.video.addEventListener('play', () => this.setState('playing'));
      this.video.addEventListener('pause', () => this.setState('paused'));
      this.video.addEventListener('ended', () => this.setState('paused'));
      this.video.addEventListener('volumechange', () => this.syncMuted());
      this.video.addEventListener('timeupdate', () => this.updateProgress());

      this.playButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onPlayClick();
      });
      this.video.addEventListener('click', () => this.onVideoClick());
      this.soundButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.video.muted = !this.video.muted;
        if (!this.video.muted) this.previewing = false;
        if (this.video.paused) this.play();
      });
      if (this.timeline) this.bindTimeline();

      if (this.autoplay) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              if (!this.userPaused) this.play();
            } else if (!this.video.paused) {
              this.video.pause();
            }
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    play() {
      const attempt = this.video.play();
      if (attempt && attempt.catch) attempt.catch(() => this.setState('paused'));
    }

    onPlayClick() {
      if (this.previewing) {
        // Silent preview → start over with sound.
        this.previewing = false;
        this.video.currentTime = 0;
        this.video.muted = false;
        this.userPaused = false;
        this.play();
        return;
      }
      if (this.video.paused) {
        if (!this.autoplay) this.video.muted = false;
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    onVideoClick() {
      if (this.previewing && this.playButton) return this.onPlayClick();
      if (this.video.paused) {
        this.userPaused = false;
        if (!this.autoplay) this.video.muted = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    setState(state) {
      this.dataset.state = this.previewing && state === 'playing' ? 'preview' : state;
      this.playButton?.setAttribute('aria-pressed', state === 'playing' && !this.previewing ? 'true' : 'false');
      if (state === 'playing') {
        this.setAttribute('data-started', '');
        this.tick();
      }
    }

    syncMuted() {
      this.toggleAttribute('data-muted', this.video.muted);
      this.soundButton?.setAttribute('aria-pressed', this.video.muted ? 'false' : 'true');
    }

    tick() {
      cancelAnimationFrame(this.frame);
      const step = () => {
        this.updateProgress();
        if (!this.video.paused) this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    updateProgress() {
      if (!this.progress || !this.video.duration) return;
      const pct = (this.video.currentTime / this.video.duration) * 100;
      this.progress.style.transform = `scaleX(${pct / 100})`;
      this.timeline.setAttribute('aria-valuenow', Math.round(pct));
    }

    bindTimeline() {
      const seek = (clientX) => {
        const rect = this.timeline.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        if (this.video.duration) this.video.currentTime = ratio * this.video.duration;
        this.updateProgress();
      };
      let dragging = false;
      this.timeline.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        dragging = true;
        this.timeline.setPointerCapture(e.pointerId);
        seek(e.clientX);
      });
      this.timeline.addEventListener('pointermove', (e) => dragging && seek(e.clientX));
      this.timeline.addEventListener('pointerup', () => (dragging = false));
      this.timeline.addEventListener('click', (e) => e.stopPropagation());
      this.timeline.addEventListener('keydown', (e) => {
        if (!this.video.duration) return;
        if (e.key === 'ArrowRight') this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + 5);
        else if (e.key === 'ArrowLeft') this.video.currentTime = Math.max(0, this.video.currentTime - 5);
        else return;
        e.preventDefault();
        this.updateProgress();
      });
    }

    loadEmbed() {
      if (this.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.src = this.dataset.embed;
      iframe.title = this.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.className = 'vvideo__media';
      this.append(iframe);
      this.dataset.state = 'playing';
      iframe.focus();
    }
  }

  customElements.define('vesper-video', VesperVideo);
})();
